/* eslint-disable jsdoc/require-param-description */
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
} from "react";
import moment from "moment";
import commissionService, {
  downloadBlob,
} from "../services/commissions.service";

const CommissionContext = createContext();

/**
 *
 * Provides global state and handlers related to commissions, such as:
 * - Exporting data (single & batch)
 * - Post-import scripts
 * - Snackbar notifications
 * - Freeze state
 *
 * @component
 * @param {object} props
 * @param {React.ReactNode} props.children - Child components to wrap with the provider
 */
export function CommissionProvider({ children }) {
  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Shared state
  const [cutoffDate, setCutoffDate] = useState(
    moment().startOf("month").format("YYYY-MM-DD")
  );
  const [updateFreeze, setUpdateFreeze] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Export states
  const [exportLoading, setExportLoading] = useState({
    fibreComms: false,
    dealers: false,
    agentTiers: false,
    advances: false,
    structure: false,
    contracts: false,
    batchExport: false,
  });
  const [exportProgress, setExportProgress] = useState(0);
  const [exportDetails, setExportDetails] = useState({});
  const [totalUsers, setTotalUsers] = useState(0);

  // Post-processing states
  const [postImportRunning, setPostImportRunning] = useState(false);
  const [postImportStatus, setPostImportStatus] = useState(null);
  const [statusCheckInterval, setStatusCheckInterval] = useState(null);

  /**
   * Display a snackbar notification.
   * @param {string} message - The message to show.
   * @param {'info' | 'success' | 'warning' | 'error'} [severity='info'] - Type of alert.
   */
  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  /**
   * Closes the snackbar.
   */
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Fetch freeze status on mount
  useEffect(() => {
    const fetchFreezeStatus = async () => {
      try {
        const status = await commissionService.getCommissionFreezeStatus();
        setUpdateFreeze(status);
      } catch (error) {
        console.error("Error fetching freeze status:", error);
        showSnackbar("Failed to get commission freeze status", "error");
      }
    };

    fetchFreezeStatus();
  }, []);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
      }
    };
  }, [statusCheckInterval]);

  /**
   * Handles exporting a specific report type.
   * @param {string} type - The report type.
   * @param {Function} exportFunction - The export service function to call.
   * @param {...any} args - Additional arguments to pass to the export function.
   */
  const handleExport = async (type, exportFunction, ...args) => {
    try {
      setExportLoading(prev => ({ ...prev, [type]: true }));

      const progressCallback = progress => {
        if (type === "dealers") {
          setExportProgress(progress);
        } else {
          setExportDetails(prev => ({
            ...prev,
            [type]: progress,
          }));
        }
      };

      const blob = await exportFunction(...args, progressCallback);

      if (blob) {
        const filename =
          type === "fibreComms"
            ? "fibre_comms_report"
            : type === "dealers"
            ? "users_master"
            : type === "agentTiers"
            ? "agent_tiers_report"
            : type === "advances"
            ? "advances"
            : type === "structure"
            ? "dealer_structure"
            : type === "contracts"
            ? "dealer_contracts"
            : "export";

        downloadBlob(blob, filename);
        showSnackbar("Export completed successfully", "success");
      } else {
        showSnackbar("No data to export", "warning");
      }
    } catch (error) {
      console.error(`${type} export error:`, error);
      showSnackbar(
        `Export failed: ${error.message || "Unknown error"}`,
        "error"
      );
    } finally {
      setExportLoading(prev => ({ ...prev, [type]: false }));
      setExportProgress(0);
    }
  };

  /**
   * Handles exporting all reports in batch.
   */
  const handleBatchExport = async () => {
    try {
      setExportLoading(prev => ({ ...prev, batchExport: true }));

      const progressCallback = progress => {
        setExportDetails(progress);
        setExportProgress(progress.progress);
      };

      const result = await commissionService.exportAllReports(
        cutoffDate,
        progressCallback
      );

      if (result.success) {
        Object.entries(result.results).forEach(([type, data]) => {
          if (data.success && data.blob) {
            const filename =
              type === "fibreComms"
                ? "fibre_comms_report"
                : type === "dealers"
                ? "users_master"
                : type === "agentTiers"
                ? "agent_tiers_report"
                : type === "advances"
                ? "advances"
                : type === "structure"
                ? "dealer_structure"
                : type === "contracts"
                ? "dealer_contracts"
                : "export";

            downloadBlob(data.blob, filename);
          }
        });

        const successful = Object.values(result.results).filter(
          r => r.success
        ).length;
        const failed = Object.values(result.results).filter(
          r => !r.success
        ).length;

        if (failed === 0) {
          showSnackbar(
            `All ${successful} reports exported successfully`,
            "success"
          );
        } else {
          showSnackbar(
            `${successful} exports succeeded, ${failed} failed. See console for details.`,
            "warning"
          );
        }
      } else {
        throw new Error(result.error || "Batch export failed");
      }
    } catch (error) {
      console.error("Batch export error:", error);
      showSnackbar(
        `Batch export failed: ${error.message || "Unknown error"}`,
        "error"
      );
    } finally {
      setExportLoading(prev => ({ ...prev, batchExport: false }));
      setExportProgress(0);
    }
  };

  /**
   * Exports all users using pagination, with progress tracking.
   */
  const handlePaginatedDealerExport = async () => {
    try {
      setExportLoading(prev => ({ ...prev, dealers: true }));
      setExportProgress(0);

      const count = await commissionService.getUserCount();
      setTotalUsers(count);

      const blob = await commissionService.exportAllUsers(
        1000,
        setExportProgress
      );

      if (blob) {
        downloadBlob(blob, "users_master");
        showSnackbar(`Successfully exported ${count} users`, "success");
      } else {
        showSnackbar("No users to export", "warning");
      }
    } catch (error) {
      console.error("Paginated export error:", error);
      showSnackbar(`Export failed: ${error.message}`, "error");
    } finally {
      setExportLoading(prev => ({ ...prev, dealers: false }));
      setExportProgress(0);
    }
  };

  /**
   * Toggles the commission update freeze state.
   */
  const handleToggleFreeze = async () => {
    try {
      const result = await commissionService.toggleCommissionFreeze();

      if (result.success) {
        setUpdateFreeze(result.updateFreeze);
        showSnackbar(result.message, "success");
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error("Error toggling freeze:", error);
      showSnackbar(`Failed to toggle freeze: ${error.message}`, "error");
    }
  };

  /**
   * Runs the post-import script and polls for its status.
   */
  const handleRunPostImport = async () => {
    try {
      setPostImportRunning(true);
      setPostImportStatus("Starting post-import process...");

      const response = await commissionService.runPostImportScript();

      if (!response.success) {
        throw new Error(
          response.error || "Failed to start post-import process"
        );
      }

      const interval = setInterval(async () => {
        const status = await commissionService.checkPostImportStatus();
        setPostImportStatus(status.message);

        if (status.complete) {
          clearInterval(interval);
          setStatusCheckInterval(null);
          setPostImportRunning(false);
          showSnackbar("Post import script completed successfully", "success");
        }
      }, 5000);

      setStatusCheckInterval(interval);
    } catch (error) {
      console.error("Post import error:", error);
      setPostImportRunning(false);
      showSnackbar(`Post import failed: ${error.message}`, "error");
    }
  };

  const value = {
    // Tab state
    activeTab,
    setActiveTab,

    // Cutoff date
    cutoffDate,
    setCutoffDate,

    // Freeze state
    updateFreeze,
    handleToggleFreeze,

    // Export functions
    handleExport,
    handleBatchExport,
    handlePaginatedDealerExport,
    exportLoading,
    exportProgress,
    exportDetails,
    totalUsers,

    // Post import
    postImportRunning,
    postImportStatus,
    handleRunPostImport,

    // Snackbar
    snackbar,
    showSnackbar,
    handleCloseSnackbar,
  };

  const memoizedValue = useMemo(() => value, [value]);

  return (
    <CommissionContext.Provider value={memoizedValue}>
      {children}
    </CommissionContext.Provider>
  );
}

/**
 * Custom hook to access commission context.
 * @returns {ReturnType<typeof CommissionProvider.value>}
 */
export const useCommissionContext = () => useContext(CommissionContext);
